// Shared worker identity block for the Shift Detail panel — the left side of both the assigned-worker
// row and a candidate row: avatar, name (optionally a profile link), the home-unit + role lines, the
// employment/shift-type/FTE meta (when the worker resolves to a roster member), and any signal tags.
// Each row supplies its own avatar tint and right-side actions; this keeps the identity layout in one
// place so the two surfaces can't drift.
import React from 'react';
import { Tooltip } from 'antd';
import { Button, Gap, Gutter, Tag, Text, UserAvatar } from '@common/components';
import { Colors } from '@common/tokens';
import type { ColorsType } from '@common/tokens';
import type {
  ScheduleCandidateSignalKind,
  ScheduleCandidateSignalType,
  ScheduleTeamMemberType,
} from '@load-balancing/types';
import {
  getEmploymentLabel,
  getShiftTypeLabel,
} from '@load-balancing/services';
import { fteTextLabel } from './_schedule-shift-detail-panel-service';
import { ScheduleDragHandle } from '@load-balancing/components/schedule-drag-handle';
import { ScheduleHomeUnit } from '@load-balancing/components/schedule-home-unit';

const SIGNAL_COLOR: Record<ScheduleCandidateSignalKind, ColorsType> = {
  positive: 'grassLightest',
  caution: 'sunLighter',
  blocking: 'coralLightest',
  neutral: 'steelLighter',
};

export function ScheduleShiftDetailWorkerIdentity({
  name,
  unit,
  role,
  member,
  bold = false,
  actionSlot,
  avatarVariant = 'default',
  signals = [],
  fteLabel,
  footer,
  dragHandle = false,
  onClick,
  unavailableTooltip,
}: {
  name: string;
  unit?: string;
  role: string;
  /** Resolved roster member; drives the employment/FTE meta. Null ⇒ meta hidden. */
  member: ScheduleTeamMemberType | null;
  /** Bold name (assigned worker) vs. medium (candidate). */
  bold?: boolean;
  /** Right-side action slot (e.g. unassign button). */
  actionSlot?: React.ReactNode;
  /** `assigned` tints the avatar elm to mark the current assignment. */
  avatarVariant?: 'default' | 'assigned';
  signals?: ScheduleCandidateSignalType[];
  /** Overrides the FTE readout in the meta line (e.g. "Xh left" for ineligible workers). */
  fteLabel?: string;
  /** Extra content below the signals (e.g. a candidate's KPI delta chips). */
  footer?: React.ReactNode;
  /** Show a drag-handle affordance beneath the avatar on hover (draggable rows). */
  dragHandle?: boolean;
  /** When set, the name renders as a profile link. */
  onClick?: () => void;
  /**
   * When set (and there's no `onClick`), the name renders non-clickable at a hyperlinked name's
   * size/weight, with this string as a hover tooltip — for a worker whose profile can't be opened.
   */
  unavailableTooltip?: string;
}) {
  const tinted = avatarVariant === 'assigned';

  return (
    <>
      <div className="schedule-shift-detail-worker-identity">
        <Gap size="sm" align="start" fullWidth>
          <Gap vertical size="lg" align="start">
            <UserAvatar
              size={34}
              uppercase
              textAsInitials={name}
              backgroundColor={
                tinted ? Colors.elmLightest : Colors.steelLighter
              }
              textColor={tinted ? Colors.elm : Colors.midnightRegular}
              borderColor={tinted ? Colors.elmLighter : Colors.steel}
            />
            {dragHandle && (
              <ScheduleDragHandle revealOnHoverOf="schedule-shift-detail-worker-identity" />
            )}
          </Gap>
          <Gutter direction="top" size="xxs" fullWidth>
            <Gap vertical size="xxs" align="start" fullWidth>
              <Gap align="center" justify="space-between" fullWidth>
                <div className="schedule-shift-detail-worker-identity__name">
                  {onClick ? (
                    <Button kind="link" onClick={onClick}>
                      {name}
                    </Button>
                  ) : unavailableTooltip ? (
                    <Tooltip title={unavailableTooltip}>
                      {/* Non-clickable, but matches a hyperlinked name's size/weight (Button kind="link"). */}
                      <span>
                        <Text size="lg" weight="bold" color="midnightDarkest">
                          {name}
                        </Text>
                      </span>
                    </Tooltip>
                  ) : (
                    <Text
                      size="md"
                      weight={bold ? 'bold' : 'medium'}
                      color="midnightDarkest"
                    >
                      {name}
                    </Text>
                  )}
                </div>
                {actionSlot && (
                  <div className="schedule-shift-detail-worker-identity__action-slot">
                    {actionSlot}
                  </div>
                )}
              </Gap>

              <ScheduleHomeUnit unitName={unit ?? ''} roleAbbreviation={role} />

              {member && (
                <Text size="md" color="midnightLighter">
                  {buildEmploymentLabel(member, fteLabel)}
                </Text>
              )}

              {signals.length > 0 && (
                <Gutter direction="top" size="xxs">
                  <Gap size="xs" wrap>
                    {signals.map((signal) => (
                      <Tag
                        key={signal.label}
                        kind="square"
                        color={SIGNAL_COLOR[signal.kind]}
                      >
                        {signal.label}
                      </Tag>
                    ))}
                  </Gap>
                </Gutter>
              )}

              {footer && (
                <Gutter direction="top" size="xxs">
                  {footer}
                </Gutter>
              )}
            </Gap>
          </Gutter>
        </Gap>
      </div>
      <style jsx>{`
        .schedule-shift-detail-worker-identity {
          width: 100%;
          max-width: 100%;
          overflow: hidden;
        }
        .schedule-shift-detail-worker-identity__name {
          max-width: 280px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      `}</style>
    </>
  );
}

// The "employment · shift-type · FTE" meta line for a roster member. FTE shows only for
// home-unit workers — cross-unit floaters have no FTE (WORK-6529/6446), so they read just
// "employment · shift-type", never a "∙ —" placeholder. `fteLabel` overrides the readout
// (e.g. "Xh left" for an ineligible home-unit worker).
function buildEmploymentLabel(
  member: ScheduleTeamMemberType,
  fteLabel?: string
): string {
  const base = `${getEmploymentLabel(member)} ${getShiftTypeLabel(member)}`;
  if (!member.homeUnitMember) return base;

  return `${base} ∙ ${fteLabel ?? fteTextLabel(member.remainingHours)}`;
}
